<template>
  <div class="product-display-container">
    <!-- 动态主题产品部分 -->
    <section v-for="topic in productTopics" :key="topic.id" class="product-section">
      <div class="section-header">
        <h2 class="section-title">{{ currentLanguage === 'zh-Hant' ? topic.name : topic.nameEn }}</h2>
        <div class="scroll-controls" v-if="topic.products.length > 4">
          <button class="scroll-btn scroll-left" @click="scrollHorizontally(topic.id, -1)" aria-label="向左滚动">
            &lt;
          </button>
          <button class="scroll-btn scroll-right" @click="scrollHorizontally(topic.id, 1)" aria-label="向右滚动">
            &gt;
          </button>
        </div>
      </div>
      <div class="product-scroll-container">
        <div class="product-scroll-wrapper" :ref="el => { scrollRefs[topic.id] = el }">
          <div v-for="product in topic.products" :key="product.id" class="product-item" @click="goToProductDetail(product, currentLanguage === 'zh-Hant' ? topic.name : topic.nameEn)">
            <div class="product-image-container">
              <img :src="currentLanguage === 'zh-Hant' ? product.imgUrl : product.imgUrlen" :alt="currentLanguage === 'zh-Hant' ? product.productName : product.productNameEn" class="product-image" />
              <div v-if="product.isNew" class="new-badge">新品</div>
              <div v-if="product.isHot" class="hot-badge">热销</div>
            </div>
            <h3 class="product-name">{{ currentLanguage === 'zh-Hant' ? product.productName : product.productNameEn }}</h3>
            <p class="product-description">{{ currentLanguage === 'zh-Hant' ? product.productDesc : product.productDescEn }}</p>
            <p class="product-price">HK${{ product.productPrice.toFixed(2) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 兼容车型标志部分 -->
    <section class="compatibility-section">
      <h2 class="compatibility-title">{{ currentLanguage === 'zh-Hant' ? '機乎可與市面上所有電動車相容' : 'Compatible with almost all electric vehicles on the market' }}</h2>
      <div class="car-brands-grid">
        <div v-for="(brand, index) in carBrands" :key="index" class="car-brand-item">
          <img :src="brand.logoUrl" :alt="brand.name" class="car-brand-logo" />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

// 主题信息接口
export interface TopicVO {
  id: number;
  name: string;
  nameEn: string;
  sort: number;
}

// 产品项接口
export interface ProductItem {
  id: number;
  imgUrl: string;
  imgUrlen: string;
  sort: number;
  productName: string;
  productNameEn: string;
  productPrice: number;
  productDesc: string;
  productDescEn: string;
  topicVO: TopicVO;
  isNew?: boolean;
  isHot?: boolean;
}

// 产品主题接口
export interface ProductTopic {
  id: number;
  name: string;
  nameEn: string;
  sort: number;
  products: ProductItem[];
}

const router = useRouter();
const { locale } = useI18n();

// 获取当前语言
const currentLanguage = ref(locale.value);

// 监听语言变化
watch(
  () => locale.value,
  (newLocale) => {
    currentLanguage.value = newLocale;
    console.log('Language changed to:', newLocale);
  },
  { immediate: true }
);

// 存储从接口获取的所有产品数据
const allProducts = ref<ProductItem[]>([]);

// 按主题分组的产品数据
const productTopics = ref<ProductTopic[]>([]);

// 滚动容器引用映射
const scrollRefs = ref<Record<string | number, HTMLElement | undefined>>({});

// 跳转到产品详情页（在当前页面打开）
const goToProductDetail = (product: ProductItem, category: string) => {
  // 使用Vue Router在当前页面导航到产品详情页
  router.push({
    path: '/product-detail',
    query: {
      category: category,
      product: JSON.stringify(product)
    }
  });
};

// 组件挂载时获取产品数据
onMounted(() => {
  fetchProductData();
});

// 从接口获取产品数据
const fetchProductData = async () => {
  try {
    const response = await fetch('/jeecg-boot/energycube/topicImg/queryAllTopicImg');
    const data = await response.json();
    if (data.code === 200 && data.result) {
      // 保存原始产品数据
      allProducts.value = data.result;
      // 按主题分组并排序
      groupProductsByTopic();
    }
    console.log("产品数据：", allProducts.value);
  } catch (error) {
    console.error('获取产品数据失败:', error);
    // 如果接口调用失败，可以使用模拟数据
    useMockData();
  }
};

// 按主题分组产品数据
const groupProductsByTopic = () => {
  // 按主题ID分组
  const topicMap = new Map<number, ProductTopic>();
  
  allProducts.value.forEach(product => {
    const topic = product.topicVO;
    if (!topicMap.has(topic.id)) {
      topicMap.set(topic.id, {
        id: topic.id,
        name: topic.name,
        nameEn: topic.nameEn,
        sort: topic.sort,
        products: []
      });
    }
    
    // 添加产品到对应主题
    topicMap.get(topic.id)?.products.push(product);
  });
  
  // 对产品排序
  topicMap.forEach(topic => {
    topic.products.sort((a, b) => a.sort - b.sort);
  });
  
  // 将Map转换为数组并按主题排序
  productTopics.value = Array.from(topicMap.values()).sort((a, b) => a.sort - b.sort);
};

// 使用模拟数据（接口调用失败时使用）
const useMockData = () => {
  const mockData: ProductItem[] = [
    {
      id: 1,
      imgUrl: 'https://picsum.photos/id/1/600/400',
      imgUrlen: 'https://picsum.photos/id/1/600/400',
      sort: 1,
      productName: 'EVlink Cross(連接線)',
      productNameEn: 'EVlink Cross(Connector)',
      productPrice: 2098,
      productDesc: '家用/公共充電站通用充電線',
      productDescEn: 'Universal charging cable for home/public charging stations',
      isNew: true,
      topicVO: {
        id: 1,
        name: '充電線',
        nameEn: 'Charging cable',
        sort: 1
      }
    },
    {
      id: 2,
      imgUrl: 'https://picsum.photos/id/2/600/400',
      imgUrlen: 'https://picsum.photos/id/2/600/400',
      sort: 2,
      productName: 'NTT DC車用充電線',
      productNameEn: 'NTT DC Vehicle Charging Cable',
      productPrice: 1898,
      productDesc: '車用充電連接線',
      productDescEn: 'Vehicle charging cable',
      isNew: false,
      topicVO: {
        id: 1,
        name: '充電線',
        nameEn: 'Charging cable',
        sort: 1
      }
    },
    {
      id: 3,
      imgUrl: 'https://picsum.photos/id/5/600/400',
      imgUrlen: 'https://picsum.photos/id/5/600/400',
      sort: 1,
      productName: 'EVlink Portable(便攜式充電器)',
      productNameEn: 'EVlink Portable(Portable Charger)',
      productPrice: 3680,
      productDesc: '家用/公共充電站通用充電線',
      productDescEn: 'Universal charging cable for home/public charging stations',
      isNew: true,
      isHot: false,
      topicVO: {
        id: 2,
        name: '便攜式充電器',
        nameEn: 'Portable charger',
        sort: 2
      }
    },
    {
      id: 4,
      imgUrl: 'https://picsum.photos/id/6/600/400',
      imgUrlen: 'https://picsum.photos/id/6/600/400',
      sort: 2,
      productName: 'EVCAR.EU EASY(便攜式充電器)',
      productNameEn: 'EVCAR.EU EASY(Portable Charger)',
      productPrice: 3480,
      productDesc: '家用/公共充電站通用充電線',
      productDescEn: 'Universal charging cable for home/public charging stations',
      isNew: false,
      isHot: true,
      topicVO: {
        id: 2,
        name: '便攜式充電器',
        nameEn: 'Portable charger',
        sort: 2
      }
    }
  ];
  
  allProducts.value = mockData;
  groupProductsByTopic();
};

// 兼容车型品牌数据
const carBrands = ref([
  { name: 'Audi', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%A5%A5%E8%BF%AA.png' },
  { name: 'Mercedes-Benz', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%A5%94%E9%A9%B0.png' },
  { name: 'BMW', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%AE%9D%E9%A9%AC%20%281%29.png' },
  { name: 'BYD', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E6%AF%94%E4%BA%9A%E8%BF%AA%20%281%29.png' },
  { name: 'Ford', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E7%A6%8F%E7%89%B9%20%281%29.png' },
  { name: 'Honda', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E6%9C%AC%E7%94%B0.png' },
  { name: 'Hyundai', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/icon_lg_hyundai.png' },
  { name: 'Nissan', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E6%97%A5%E4%BA%A7.png' },
  { name: 'Lexus', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E9%9B%B7%E5%85%8B%E8%90%A8%E6%96%AF.png' },
  { name: 'Porsche', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/porsche%20%281%29.png' },
  { name: 'Volvo', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/volvo%20%281%29.png' },
  { name: 'Volkswagen', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/volkswagen.png' },
  { name: 'Kia', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/kia.png' },
  { name: 'Maserati', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/maserati.png' },
  { name: '吉利', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%90%89%E5%88%A9%E9%93%B6%E6%B2%B3.png' },
  { name: 'Tesla', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/Tesla.png' },
  { name: 'Toyota', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/toyota.png' },
  { name: '奇瑞', logoUrl: 'https://rock-printer.oss-cn-shenzhen.aliyuncs.com/brand_logo/%E5%A5%87%E7%91%9E.png' }
]);

// 水平滚动函数
function scrollHorizontally(topicId: number | string, direction: number) {
  const scrollRef = scrollRefs.value[topicId];
  const scrollAmount = 300; // 每次滚动的距离

  // 执行滚动
  if (scrollRef) {
    scrollRef.scrollBy({
      left: scrollAmount * direction,
      behavior: 'smooth'
    });
  }
}
</script>

<style scoped>
.product-display-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.product-section {
  margin-bottom: 60px;
  position: relative;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.section-title {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  flex: 1;
  text-align: center;
}

/* 滚动控制按钮 */
.scroll-controls {
  position: absolute;
  top: 50%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  transform: translateY(-50%);
  z-index: 10;
}

.scroll-btn {
  pointer-events: all;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  font-size: 20px;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.scroll-btn:hover {
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: scale(1.1);
}

/* 滚动容器样式 */
.product-scroll-container {
  position: relative;
  overflow: hidden;
  margin: 0 -10px;
}

.product-scroll-wrapper {
  display: flex;
  gap: 30px;
  padding: 0 10px;
  transition: transform 0.3s ease;
  scroll-behavior: smooth;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
}

.product-scroll-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Edge */
}

.product-item {
  flex: 0 0 auto;
  width: 280px;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.product-item {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.product-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.product-image-container {
  position: relative;
  overflow: hidden;
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.product-item:hover .product-image {
  transform: scale(1.05);
}

.new-badge,
.hot-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  color: #fff;
}

.new-badge {
  background-color: #e74c3c;
}

.hot-badge {
  background-color: #f39c12;
}

.product-name {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin: 15px 15px 10px;
}

.product-description {
  font-size: 14px;
  color: #666;
  margin: 0 15px 10px;
  line-height: 1.4;
}

.product-price {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin: 0 15px 15px;
}

.compatibility-section {
  background-color: #f8f8f8;
  padding: 40px 20px;
  border-radius: 12px;
  margin-top: 60px;
}

.compatibility-title {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 30px;
  text-align: center;
}

.car-brands-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 30px;
  justify-items: center;
}

.car-brand-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.car-brand-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.car-brand-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .product-item {
    width: 240px;
  }
  
  .scroll-btn {
    width: 35px;
    height: 35px;
    font-size: 18px;
  }
  
  .car-brands-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 768px) {
  .product-item {
    width: 200px;
  }
  
  .scroll-btn {
    width: 30px;
    height: 30px;
    font-size: 16px;
  }
  
  .product-display-container {
    padding: 20px 15px;
  }
  
  .section-title {
    font-size: 24px;
  }
  
  .compatibility-title {
    font-size: 20px;
  }
  
  .product-scroll-wrapper {
    gap: 20px;
  }
  
  .car-brands-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 480px) {
  .product-item {
    width: 160px;
  }
  
  .car-brand-item {
    width: 80px;
    height: 80px;
  }
  
  .scroll-controls {
    display: none;
  }
  
  .car-brands-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  /* 移动端允许手动滚动 */
  .product-scroll-wrapper {
    -webkit-overflow-scrolling: touch;
  }
}
</style>